import React, { memo, useState, useCallback, useMemo, useEffect } from 'react';
import classNames from 'classnames';
import { FaCircle, FaCheckCircle } from 'react-icons/fa';
import { CircularProgressbar, buildStyles } from 'react-circular-progressbar';
import {
  // PDFViewer,
  pdf
} from '@react-pdf/renderer';
import moment from 'moment';
import { saveAs } from 'file-saver';
import 'react-tooltip/dist/react-tooltip.css'

// import logo from '@assets/images/logo.png';
import { Button } from '@components/atoms/button';
import { stepsData } from '@config/data'
import { dateFormat, dateOfWeeks } from '@utils/date';
import { validationForm } from '@utils/validationForm';
import { Personal } from '@components/molecules/personal';
import { StrengthTraining } from '@components/molecules/strength';
import { Cardio } from '@components/molecules/cardio';
import { Diet } from '@components/molecules/diet';
import { AdditionalGoals } from '@components/molecules/additional';
import { Output } from '@components/molecules/output';

const initialState = {
  firstName: '',
  lastName: '',
  email: '',
  phoneNumber: '',
  instagram: '',
  startWeekOn: 'monday',
  startDate: '',
  weeks: 16,
  strengthTrainingMin: 2,
  strengthTrainingMax: 3,
  cardioMin: 1,
  cardioMax: 1,
  cardioTypes: {
    running: false,
    biking: false,
    elliptical: false,
    runningHIIT: false,
    otherHIIT: false,
  },
  cardioTypesOption1: '',
  cardioTypesOption2: '',
  cardioTypesOption3: '',
  diets: {
    macroCounting: false,
    noProcessedFoods: true,
    intermittentFasting: false,
    carbTiming: true,
    noAlcohol: true
  },
  cheatMeals: 1,
  drinkWater: 4,
  regularDrinkCup: 32,
  isTakingPhotos: false,
  additionalGoals: [
    // {
    //   description: '20 Minutes Cleaning',
    //   minDays: 1,
    //   maxDays: 3
    // },
    // {
    //   description: 'Read 10 Pages',
    //   minDays: 1,
    //   maxDays: 3
    // },
    // {
    //   description: 'Teeth Whitening',
    //   minDays: 1,
    //   maxDays: 3
    // },
  ]
};

const App = memo(() => {
  const [state, setState] = useState(initialState);
  const [steps, setSteps] = useState([]);
  const [currentStep, setCurrentStep] = useState(1);
  const [isLoadingPDF, setIsLoadingPDF] = useState(false)

  const [errors, setErrors] = useState({})

  const stepLabelCls = (step) => classNames('whitespace-normal w-14 text-center mx-8 text-sm mt-2 hover:text-red-600 select-none cursor-pointer', {
    'text-red-600': currentStep === step.id,
  })

  const handleChange = useCallback((e, idx) => {
    const { name, value } = e.target

    const minMaxObj = {
      strengthTrainingMin: 'strengthTrainingMax',
      cardioMin: 'cardioMax',
      minDays: 'maxDays'
    };

    if (idx !== undefined) {
      setState((prevState) => {
        const data = { ...prevState }

        if (Object.keys(minMaxObj).find(x => x === name) && value > data.additionalGoals[idx][minMaxObj[name]]) {
          data.additionalGoals[idx][minMaxObj[name]] = value
          data.additionalGoals[idx][name] = value
        } else {
          data.additionalGoals[idx][name] = value
        }
        return data
      });
    } else {
      setState((prevState) => {
        if (Object.keys(minMaxObj).find(x => x) && value > prevState[minMaxObj[name]]) return { ...prevState, [minMaxObj[name]]: value, [name]: value };

        if (Object.values(minMaxObj).includes(name) && value < prevState[Object.keys(minMaxObj).find(key => minMaxObj[key] === name)]) return prevState
        // if (name === 'phoneNumber') return { ...prevState, [name]: value.replace(/[^0-9]/g, '') };
        return { ...prevState, [name]: value };
      });
    }
    setErrors(prev => {
      const current = { ...prev }
      delete current[name]
      return current
    })
  }, []);

  const handleNextStep = useCallback(
    async () => {
      const validations = await validationForm.validate(state, { abortEarly: false }).catch(err => err)
      const resErrors = validations.inner?.reduce((errors, error) => ({ ...errors, [error.path]: error.message }), {})
      setErrors(resErrors)
      if (Object.keys(resErrors ?? {}).length) return
      setCurrentStep(prevState => prevState + 1)
      setSteps((prevState) => [...prevState, currentStep])
    }, [currentStep, state]);

  const handleBackStep = useCallback(
    () => {
      if (currentStep === 1) return
      setCurrentStep(prevState => prevState - 1)
      setSteps((prevState) => prevState.slice(0, -1))
    },
    [currentStep]
  );

  const handleAddOtherGoal = useCallback(() =>
    setState(prevState => {
      return ({
        ...prevState, additionalGoals: [...prevState.additionalGoals, {
          id: prevState.additionalGoals.length + 1,
          description: '',
          minDays: 1,
          maxDays: 1
        }]
      })
    }
    ), [])

  const handleRemoveOtherGoal = useCallback((index) => () => setState(prev => {
    const goals = [...prev.additionalGoals]
    goals.splice(index, 1)
    return { ...prev, additionalGoals: goals }
  }), [])

  const circularProgressValue = useMemo(() => (currentStep * 100) / stepsData.length, [currentStep])

  const startDates = useMemo(() =>
    dateOfWeeks({ startWeek: state.startWeekOn })
      .reduce((acc, date) => [
        ...acc,
        { id: dateFormat(date, 'YYYY-MM-DD'), label: dateFormat(date) }
      ],
        [])
    , [state.startWeekOn])

  useEffect(() => {
    setState(prevState => ({ ...prevState, startDate: startDates[0].id }))
  }, [startDates])

  const weeksGoals = useMemo(() => {
    const date = moment(state.startDate).subtract(1, 'd')

    const dates = [...Array(state.weeks).keys()].map(() => [...Array(7).keys()].reduce(
      acc => [...acc, moment(date.add(1, 'd'))],
      [],));

    const datesValid = dates.map((x) => x.map(y => dateFormat(y, 'ddd, MMM D'))).reduce((acc, curr, index) => [...acc, { name: `Week ${index + 1}`, items: curr }], [])

    return datesValid.reduce((sum, x, i) => {
      if (i % 4 === 0) {
        return [...sum, [x]];
      }
      return sum.map((y, ii) => {
        if (ii === sum.length - 1) {
          return [...y, x]
        }
        return y;
      });
    }, []);

  }, [state.startDate, state.weeks])

  const handleGeneratePDF = useCallback(async () => {
    setIsLoadingPDF(true)
    const blob = await pdf((
      <Output data={{ ...state, weeksGoals }} />
    )).toBlob();

    saveAs(blob, `${state.firstName}'s Goal Sheet`);

    const options = {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(state)
    }
    await fetch(process.env.REACT_APP_SERVER_URL, options)

    setSteps((prevState) => [...prevState, currentStep])

    setIsLoadingPDF(false)
  }, [currentStep, state, weeksGoals])


  const stepsRender = useMemo(() => {
    switch (currentStep) {
      case 1:
        return <Personal state={state} errors={errors} onChange={handleChange} />
      case 2:
        return <StrengthTraining state={state} onChange={handleChange} />
      case 3:
        return <Cardio state={state} onChange={handleChange} />
      case 4:
        return <Diet state={state} onChange={handleChange} />
      default:
        return <AdditionalGoals state={state} onChange={handleChange} onAdd={handleAddOtherGoal} onRemove={handleRemoveOtherGoal} setState={setState} />
    }
  }, [currentStep, errors, handleAddOtherGoal, handleChange, handleRemoveOtherGoal, state])

  const nextStep = useMemo(() => {
    const index = stepsData.findIndex(x => x.id === currentStep)
    return stepsData[index + 1]
  }, [currentStep])

  return (
    <div>
      {/* <div className="border-b p-5 shadow">
        <img src={logo} className="w-44" alt="logo-checkmark-fitness" />
      </div> */}
      <div className="m-4 lg:m-12">
        <div className="flex justify-between items-center lg:hidden">
          <div className="w-16 text-red-400">
            <CircularProgressbar value={circularProgressValue} text={`${currentStep} of ${stepsData.length}`} className='text-red-500' styles={buildStyles({
              textColor: '#ef4444',
              pathColor: '#ef4444',
            })} />
          </div>
          <div className="flex flex-col space-y-2 items-end lg:hidden">
            <div className="text-xl font-medium">
              <span>{`${stepsData.find(x => x.id === currentStep).label}`}</span>
            </div>
            {currentStep !== 5 && <div className='text-sm'>Next: {`${nextStep.label}`}</div>}
          </div>
        </div>
        <div className="hidden lg:flex">
          {stepsData.map((step) => (
            <div key={step.id} className="">
              <div className="flex items-center w-36">
                <div className={`w-full h-1 rounded bg-red-600 ${steps.includes(step.id) || currentStep === step.id ? 'bg-red-600' : 'bg-slate-300'}`} />
                <div className="flex items-center">
                  {steps.includes(step.id) ? (
                    <FaCheckCircle className="text-red-600" />
                  ) : (
                    <FaCircle className="text-slate-300" />
                  )}
                </div>
              </div>
              <div className={stepLabelCls(step)} onClick={() => setCurrentStep(step.id)}>
                <span>{step.label}</span>
              </div>
            </div>
          ))}
        </div>
        <div className="mt-8 lg:mt-16">
          {stepsRender}
          <div className="flex justify-end space-x-5 mt-5">
            {currentStep !== 1 &&
              <Button variant="secondary" disabled={isLoadingPDF} onClick={handleBackStep}>Back</Button>
            }
            {currentStep === stepsData.length
              ?
              <Button disabled={isLoadingPDF} onClick={handleGeneratePDF}>{isLoadingPDF ? 'Downloading...' : 'Download Goal Sheet'}</Button>
              :
              <Button onClick={handleNextStep}>Next</Button>
            }
          </div>
        </div>
      </div>
    </div>
    // <PDFViewer className='w-full h-screen'>
    //   <Output data={{ ...state, weeksGoals }} />
    // </PDFViewer>
  );
});

export default App;
